/**
 * Analytics types for Midjen engagement logging
 *
 * Based on Meta's Ecto logging patterns for product engagement tracking.
 */

import type { QPLEventType, QPLActions, BaseTrace } from '@/lib/qpl/types';

/**
 * Page-level route trace policies for the Midjen application.
 *
 * Used for navigation tracking and initial page loads (cold starts).
 * Follows naming convention: "midjen.{pageName}" or "midjen.{feature}.{subPage}"
 */
export enum MidjenRouteTracePolicy {
  // Main routes
  HOME = 'midjen.home',
  CREATE = 'midjen.create',
  IMPORT = 'midjen.import',

  // Pro routes
  PRO_CREATE = 'midjen.pro.create',
  PRO_EDIT = 'midjen.pro.edit',

  // Project routes (canonical URLs for /projects)
  PROJECTS = 'midjen.projects',
  PROJECT_DETAIL = 'midjen.project.detail',
  PROJECT_CONTENT_EDIT = 'midjen.project.content.edit',

  // Media Library routes
  MEDIA_LIBRARY = 'midjen.media_library',

  // User routes
  ACCOUNT = 'midjen.account',

  // Admin routes
  ADMIN = 'midjen.admin',

  // Deep link landing pages
  SPACE_INVITE = 'midjen.space.invite',

  // Other routes
  PLAYABLES = 'midjen.playables',
  PLAYABLE_PREVIEW_PAGE = 'midjen.playables.preview',
  STATS = 'midjen.stats',

  // Story Mode routes
  STORIES = 'midjen.stories',
  STORY_DETAIL = 'midjen.story.detail',
}

/**
 * Interaction-level trace policies for the Midjen application.
 *
 * Used for tracking user interactions like button clicks, form submissions, etc.
 * Format: "midjen.{feature}.{action}"
 */
export enum MidjenInteractionTracePolicy {
  // Generation interactions
  GENERATION_START = 'midjen.generation.start',
  GENERATION_COMPLETE = 'midjen.generation.complete',
  GENERATION_CANCEL = 'midjen.generation.cancel',

  // Import interactions
  IMPORT_MEDIA = 'midjen.import.media',
  IMPORT_PREVIEW = 'midjen.import.preview',

  // Media interactions
  MEDIA_DOWNLOAD = 'midjen.media.download',
  MEDIA_UPLOAD = 'midjen.media.upload',
  MEDIA_UPLOAD_FILE = 'midjen.media.upload_file',
  UPLOAD_IMAGE = 'midjen.upload.image',
  MEDIA_VIEW = 'midjen.media.view',
  MEDIA_SHARE = 'midjen.media.share',

  // Moodboard interactions
  MOODBOARD_CREATE = 'midjen.moodboard.create',
  MOODBOARD_EDIT = 'midjen.moodboard.edit',
  MOODBOARD_DELETE = 'midjen.moodboard.delete',

  // Moodboard API (server-side)
  MOODBOARD_API_LIST = 'midjen.moodboard.api_list',
  MOODBOARD_API_CREATE = 'midjen.moodboard.api_create',
  MOODBOARD_API_GET = 'midjen.moodboard.api_get',
  MOODBOARD_API_PATCH = 'midjen.moodboard.api_patch',

  // Composer interactions
  COMPOSER_SUBMIT = 'midjen.composer.submit',
  COMPOSER_PRESET_APPLY = 'midjen.composer.preset_apply',

  // Playables interactions (server-side)
  PLAYABLE_GENERATE = 'midjen.playable.generate',
  PLAYABLE_STREAM_INTERRUPTED = 'midjen.playable.stream_interrupted',
  PLAYABLE_PUBLISH = 'midjen.playable.publish',
  PLAYABLE_NOTIFY_PUBLISHED = 'midjen.playable.notify_published',
  PLAYABLE_THUMBNAIL = 'midjen.playable.thumbnail',
  PLAYABLE_THUMBNAIL_UPLOAD = 'midjen.playable.thumbnail_upload',
  PLAYABLE_THUMBNAIL_RESOLVE = 'midjen.playable.thumbnail_resolve',
  PLAYABLE_PREVIEW_VIDEO_UPLOAD = 'midjen.playable.preview_video_upload',
  PLAYABLE_POST_URL = 'midjen.playable.post_url',
  PLAYABLE_PREVIEW = 'midjen.playable.preview',

  // Bug report (server-side)
  BUG_REPORT_SUBMIT = 'midjen.bug_report.submit',

  // Asset upload (server-side)
  UPLOAD_ASSET = 'midjen.upload.asset',
  UPLOAD_AUDIO = 'midjen.upload.audio',
  UPLOAD_AUDIO_DIRECT = 'midjen.upload.audio_direct',
  UPLOAD_VIDEO_DIRECT = 'midjen.upload.video_direct',
  UPLOAD_MUSIC_TO_OIL = 'midjen.upload.music_to_oil',

  // Abra GraphQL API interactions (server-side)
  ABRA_VIEWER_PROFILE = 'midjen.abra.viewer_profile',
  ABRA_FULL_PROFILE = 'midjen.abra.full_profile',
  ABRA_GENERATION_FEEDBACK = 'midjen.abra.generation_feedback',
  ABRA_CREATE_SOCIAL_PROFILE = 'midjen.abra.create_social_profile',
  ABRA_VIBES_ELIGIBILITY = 'midjen.abra.vibes_eligibility',

  // Profile picture upload (server-side)
  UPLOAD_PROFILE_PICTURE = 'midjen.upload.profile_picture',

  // Generation API interactions (server-side)
  GENERATE_PROMPTS = 'midjen.generate.prompts',
  GENERATE_IMAGES = 'midjen.generate.images',
  GENERATE_VIDEOS = 'midjen.generate.videos',
  GENERATE_FACE_SWAP = 'midjen.generate.face_swap',

  // Auth API interactions (server-side)
  AUTH_LOGOUT = 'midjen.auth.logout',
  AUTH_OIDC_START = 'midjen.auth.oidc_start',
  AUTH_OIDC_CALLBACK = 'midjen.auth.oidc_callback',
  AUTH_CHECK_TOKEN = 'midjen.auth.check_token',

  // Meta GraphQL proxy (server-side)
  META_GRAPHQL_PROXY = 'midjen.meta.graphql_proxy',

  // Music library search (server-side)
  MUSIC_SEARCH = 'midjen.music.search',
  MUSIC_OA_CHECK = 'midjen.music.oa_check',

  // Music track lookup (server-side)
  META_MUSIC_LOOKUP = 'midjen.music.lookup',

  // Audio batch resolution (server-side)
  RESOLVE_AUDIO_URLS = 'midjen.audio.resolve_urls',

  // Timeline/Studio export and download (server-side)
  TIMELINE_EXPORT = 'midjen.timeline.export',
  TIMELINE_EXPORT_CLIENT = 'midjen.timeline.export_client',
  TIMELINE_EXPORT_CACHE_CHECK = 'midjen.timeline.export_cache_check',
  TIMELINE_DOWNLOAD = 'midjen.timeline.download',
  SURFGUARD_EXPORT = 'midjen.surfguard.export',
  SURFGUARD_EXPORT_STATUS = 'midjen.surfguard.export_status',
  SURFGUARD_EXPORT_PENDING = 'midjen.surfguard.export_pending',
  SURFGUARD_EXPORT_CANCEL = 'midjen.surfguard.export_cancel',
  SURFGUARD_EXPORT_PIPELINE = 'midjen.surfguard.export_pipeline',

  // Project Assets API (server-side)
  PROJECT_ASSETS_LIST = 'midjen.projectassets.list',
  PROJECT_DUPLICATE = 'midjen.project.duplicate',
  PLAYABLE_DUPLICATE = 'midjen.playable.duplicate',

  // Projects API (server-side)
  PROJECTS_LIST = 'midjen.projects.list',

  // Generation Batches API (server-side)
  GENERATION_BATCHES_CREATE = 'midjen.generationbatches.create',
  GENERATION_BATCHES_STREAM = 'midjen.generationbatches.stream',

  // Project Batches API (server-side)
  PROJECT_BATCHES_LIST = 'midjen.projectbatches.list',

  // Admin Bulk Playables (server-side)
  ADMIN_BULK_PLAYABLES = 'midjen.admin.bulk_playables',

  // Admin Stats API interactions (server-side)
  ADMIN_STATS_USERS = 'midjen.admin.stats_users',
  ADMIN_STATS_CONTENT = 'midjen.admin.stats_content',
  ADMIN_STATS_PUBLISHING = 'midjen.admin.stats_publishing',
  ADMIN_STATS_STUDIO = 'midjen.admin.stats_studio',
  ADMIN_STATS_ACTIVITY = 'midjen.admin.stats_activity',
  ADMIN_STATS_GENERATIONS = 'midjen.admin.stats_generations',
  ADMIN_STATS_ENGAGEMENT = 'midjen.admin.stats_engagement',
  ADMIN_STATS_ACTIVE_USERS = 'midjen.admin.stats_active_users',

  // Per-user stats API interactions (server-side)
  ADMIN_USER_STATS_GENERATIONS = 'midjen.admin.user_stats_generations',
  ADMIN_USER_STATS_ACTIVITY = 'midjen.admin.user_stats_activity',
  ADMIN_USER_STATS_ERRORS = 'midjen.admin.user_stats_errors',

  // Deletion interactions (server-side)
  CONTENT_ITEMS_BULK_DELETE = 'midjen.content_items.bulk_delete',
  PROJECT_DELETE = 'midjen.project.delete',
  PROJECT_ASSET_REMOVE = 'midjen.project.asset_remove',
  BATCH_DELETE = 'midjen.batch.delete',
  CONTENT_ITEM_DELETE = 'midjen.content_item.delete',
  CONTENT_ITEM_FAVORITE_TOGGLE = 'midjen.content_item.favorite_toggle',
  CONTENT_ITEM_RETRY = 'midjen.content_item.retry',
  SETTINGS_DELETE_ALL_MEDIA = 'midjen.settings.delete_all_media',

  // Media Library
  MEDIA_LIBRARY_LIST = 'midjen.media_library.list',
  MEDIA_LIBRARY_GET = 'midjen.media_library.get',
  MEDIA_LIBRARY_DELETE = 'midjen.media_library.delete',
  MEDIA_LIBRARY_PROJECTS = 'midjen.media_library.projects',
  MEDIA_LIBRARY_INGREDIENT_CREATE = 'midjen.media_library.ingredient_create',
  MEDIA_LIBRARY_INGREDIENT_UPDATE = 'midjen.media_library.ingredient_update',
  MEDIA_LIBRARY_DETAIL_MEDIA_LOAD = 'midjen.media_library.detail_media_load',
  SETTINGS_REMOVE_ALL_POSTS = 'midjen.settings.remove_all_posts',
  SETTINGS_DELETE_ACCOUNT = 'midjen.settings.delete_account',

  // Quota upsell (server-side)
  QUOTA_UPSELL = 'midjen.quota.upsell',

  // Lipsync generation (server-side)
  ANIMATE_GENERATE = 'midjen.animate.generate',

  // Voice picker (server-side)
  VOICE_PICKER = 'midjen.voice.picker',

  // Ingredients queries — Library + Viewer (server-side)
  INGREDIENTS = 'midjen.ingredients.query',

  // Ingredient creation (server-side)
  INGREDIENT_CREATE = 'midjen.ingredient.create',

  // Ingredient projects (server-side)
  INGREDIENT_PROJECTS = 'midjen.ingredient.projects',

  // Ingredient deletion (server-side)
  INGREDIENT_DELETE = 'midjen.ingredient.delete',

  // Ingredient bulk deletion (server-side)
  INGREDIENT_BULK_DELETE = 'midjen.ingredient.bulk_delete',

  // Ingredient update (server-side)
  INGREDIENT_UPDATE = 'midjen.ingredient.update',

  // Text Overlay interactions (client-side)
  TEXT_OVERLAY_ADD = 'midjen.text_overlay.add',
  TEXT_OVERLAY_EDIT = 'midjen.text_overlay.edit',
  TEXT_OVERLAY_PRESET_APPLY = 'midjen.text_overlay.preset_apply',
  TEXT_OVERLAY_POSITION = 'midjen.text_overlay.position',
  TEXT_OVERLAY_RESIZE = 'midjen.text_overlay.resize',

  // Audio track interactions (client-side)
  AUDIO_TRACK_ADD_MUSIC = 'midjen.audio_track.add_music',
  AUDIO_TRACK_ADD_VOICEOVER = 'midjen.audio_track.add_voiceover',
  AUDIO_TRACK_REMOVE = 'midjen.audio_track.remove',
  AUDIO_TRACK_VOLUME_CHANGE = 'midjen.audio_track.volume_change',
  AUDIO_TRACK_MUTE = 'midjen.audio_track.mute',
  AUDIO_TRACK_SLIP = 'midjen.audio_track.slip',

  // Video clip interactions (client-side)
  VIDEO_CLIP_REMOVE = 'midjen.video_clip.remove',
  VIDEO_CLIP_REORDER = 'midjen.video_clip.reorder',
  VIDEO_CLIP_TRIM = 'midjen.video_clip.trim',
  VIDEO_CLIP_SPLIT = 'midjen.video_clip.split',
  VIDEO_CLIP_DUPLICATE = 'midjen.video_clip.duplicate',

  // Timeline general interactions (client-side)
  TIMELINE_PLAYBACK_START = 'midjen.timeline.playback_start',
  TIMELINE_PLAYBACK_PAUSE = 'midjen.timeline.playback_pause',
  TIMELINE_ZOOM = 'midjen.timeline.zoom',
  TIMELINE_UNDO = 'midjen.timeline.undo',
  TIMELINE_REDO = 'midjen.timeline.redo',

  // Revision API interactions (server-side)
  REVISION_CREATE = 'midjen.revision.create',
  REVISION_LIST = 'midjen.revision.list',
  REVISION_GET = 'midjen.revision.get',
  REVISION_DELETE = 'midjen.revision.delete',
  REVISION_RENAME = 'midjen.revision.rename',

  // Content export (client-side: save flush → modal open)
  CONTENT_EXPORT_CLIENT = 'midjen.content.export_client',

  // Content publish (client-side: post button → API response)
  CONTENT_PUBLISH_CLIENT = 'midjen.content.publish_client',

  // Gallery
  GALLERY_LIST = 'midjen.gallery.list',
  GALLERY_CREATORS = 'midjen.gallery.creators',

  // Timeline agentic chat
  TIMELINE_CHAT = 'midjen.timeline.chat',

  // Story Mode API (server-side)
  STORY_LIST = 'midjen.story.list',
  STORY_LOAD = 'midjen.story.load',
  STORY_CREATE = 'midjen.story.create',
  STORY_ADVANCE = 'midjen.story.advance',

  // Story Mode tool calls (kernel-driven)
  GENERATE_STYLE_REF = 'midjen.story.generate_style_ref',
  GENERATE_CHARACTER_REFS = 'midjen.story.generate_character_refs',
  GENERATE_IMAGE_ASSETS = 'midjen.story.generate_image_assets',
  GENERATE_VIDEO_ASSETS = 'midjen.story.generate_video_assets',
  GENERATE_AUDIO_ASSETS = 'midjen.story.generate_audio_assets',
  SELECT_MUSIC_TRACK = 'midjen.story.select_music_track',
  ADVANCE_TO_ASSETS = 'midjen.story.advance_to_assets',
}

/**
 * Action types for user interactions
 */
export type AnalyticsActionType =
  | 'click'
  | 'dismiss'
  | 'drag'
  | 'hover'
  | 'key_command'
  | 'one_px_impression'
  | 'press_enter'
  | 'system_event'
  | 'time_spent'
  | 'type'
  | 'vpv_impression';

/**
 * Target areas where interactions occur.
 * Helps segment analytics by UI region.
 */
export type AnalyticsTargetArea =
  | 'header_nav'
  | 'side_nav'
  | 'composer'
  | 'gallery'
  | 'media_viewer'
  | 'modal'
  | 'context_menu'
  | 'toolbar'
  | 'settings'
  | 'footer'
  | 'content_area'
  | 'sidebar'
  | 'timeline'
  | 'page'
  | 'unknown';

/**
 * Click items for specific interaction tracking
 */
export type AnalyticsClickItem =
  | 'click'
  | 'create'
  | 'delete'
  | 'download'
  | 'share'
  | 'copy'
  | 'login'
  | 'logout'
  | 'settings'
  | 'help'
  | 'cancel'
  | 'confirm'
  | 'close';

/**
 * Base interaction event structure
 */
export interface InteractionEvent {
  /** Name of the component it came from */
  actionTarget: string;
  /** Type of action */
  actionType: AnalyticsActionType;
  /** Specific item that was clicked */
  clickItem?: AnalyticsClickItem;
  /** Name of the event */
  eventName: string;
  /** Current URL */
  httpReferrer: string;
  /** Interface type */
  interface: 'desktop_web' | 'mobile_web';
  /** Target area where the interaction occurred */
  targetArea?: AnalyticsTargetArea;
  /** Timestamp when event occurred (milliseconds since epoch) */
  timestamp: number;
  /** User's ID (added server-side) */
  userID?: string;
  /** Current Page Trace Policy */
  tracePolicyReferrer: string;
  /** Additional metadata for entity-specific tracking */
  metadata?: Record<string, unknown>;
}

/**
 * Navigation event structure
 */
export interface NavigationEvent {
  /** Route-level trace policy for the page (e.g., midjen.home) */
  tracePolicy: string;
  /** QPL event type (INITIAL_LOAD or NAVIGATION) */
  qplEvent: QPLEventType;
  /** QPL action (SUCCESS for completed navigations) */
  qplAction: QPLActions;
  /** Start time in milliseconds */
  startTime: number;
  /** End time in milliseconds */
  endTime: number;
  pathname: string;
  timestamp: number;
  duration?: number;
  previousPathname?: string;
  previousTracePolicy?: string;
  userID?: string;
  /** Document referrer URL */
  referrer?: string;
  /** User agent string */
  userAgent?: string;
  /** Viewport dimensions */
  viewport?: {
    width: number;
    height: number;
  };
}

/**
 * Time spent event for tracking page/component engagement duration
 * Uses Page Visibility API to only track active (visible) time
 */
export interface TimeSpentEvent {
  tracePolicy: string;
  eventName: string;
  actionType: 'time_spent';
  actionTarget: string;
  targetArea: AnalyticsTargetArea;
  /** Duration in milliseconds of active (visible) time */
  duration: number;
  timestamp: number;
  /** User ID - added by the server */
  userID?: string;
  /** Additional metadata for entity-specific tracking */
  metadata?: Record<string, unknown>;
}

/**
 * Web Vitals event for tracking Core Web Vitals metrics
 * Sent once per page load after vitals stabilize
 * Uses QPL points for time-based metrics and annotations for scores/metadata
 */
export interface WebVitalsEvent {
  /** Route-level trace policy for the page */
  tracePolicy: string;
  /** QPL event type (INITIAL_LOAD) */
  qplEvent: QPLEventType;
  /** QPL action (SUCCESS or FAIL) */
  qplAction: QPLActions;
  /** Page pathname */
  pathname: string;
  /** Timestamp when event was sent */
  timestamp: number;
  /** Navigation start time for calculating point offsets */
  startTime: number;
  /** End time (startTime + LCP or latest vital) for duration calculation */
  endTime: number;
  /** QPL points for time-based web vital metrics (TTFB, FCP, LCP) */
  points: Array<{
    /** Point name (e.g., 'ttfb', 'fcp', 'lcp') */
    name: string;
    /** Time since start in nanoseconds */
    timeSinceStartNs: number;
    /** Point data with typed values */
    data?: {
      int?: Record<string, number>;
    };
  }>;
  /** String annotations (e.g., LCP element selector) */
  annotations?: Record<string, string>;
  /** Double annotations (e.g., CLS score) */
  annotationsDouble?: Record<string, number>;
}

/**
 * All valid event types that can be logged
 */
export type ValidEvent = InteractionEvent | NavigationEvent | TimeSpentEvent | WebVitalsEvent;

/**
 * Logging package structure (batch of events)
 */
export interface LoggingPackage {
  events: ValidEvent[];
  timestamp: number;
  initialLoad?: BaseTrace;
  navigations?: BaseTrace[];
  publishes?: BaseTrace[];
}
